import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import isFunction from 'lodash/isFunction';
import union from 'lodash/union';
import get from 'lodash/get';
import UniprotPdbLinks from '@Components/Results/targets/UniprotPdbLinks';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ReactReactionDetailsHelper from '@Components/Results/services/react-reaction-details-helper.service';
import ReactTargetHelper from '@Components/Results/services/react-target-helper.service';
import Synonyms from '@Components/Results/targets/Synonyms';
import * as ResultsDataManage from '@Services/ResultsDataManage';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import Breadcrumb from '@Services/Breadcrumb';
import { enrichParamsWithSemanticFlags } from '@Pages/Results/semanticSearchUtils';

function getTrackingAttrs(target, metadata) {
  return {
    'data-click-delegation': 'true',
    'data-tracking': 'true',
    'data-tracking-context': 'citations',
    'data-tracking-preview': metadata.isViewPreview,
    ...PrepareTrackingDataHelper.prepare(target),
  };
}

function DocumentTargets({ metadata, documentIndex, documentRelativeIndex, databaseId, targetsCount, closeClick, minCount = 3 }) {
  const [targets, setTargets] = useState([]);
  const [totalItems, setTotalItems] = useState(targetsCount);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRequestRef = useRef(null);

  const processTargetsResponse = useCallback(
    (data) => {
      setTargets((prevTargets) =>
        union(
          prevTargets,
          data.map((target) => ({ ...target, databaseId }))
        )
      );
      const resultId = `${metadata.resultName}/${documentIndex}`;
      const newTotalItems = ResultsDataManage.getTargetsTotal(resultId);
      if (newTotalItems && newTotalItems !== totalItems) {
        setTotalItems(newTotalItems);
      }
    },
    [databaseId, documentIndex, metadata, totalItems]
  );

  const getTargetsForCitations = useCallback(
    (ignoreCitRef = false) => {
      const context = 'tgitems';
      const currentCount = targets.length;
      setIsLoading(true);

      const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, metadata?.resultName);
      const paramsWithSemantic = enrichParamsWithSemanticFlags(
        {
          resultName: metadata.resultName,
          resultIndex: documentIndex,
          resultRelativeIndex: documentRelativeIndex,
          countOfCurrentItems: currentCount,
          totalItems,
          databaseId,
          sort: metadata.sort,
          order: metadata.order,
          context,
          count: minCount,
          ignoreCitRef,
        },
        activeBreadcrumb
      );

      const req = ResultsDataManage.getItemsForCitations(paramsWithSemantic)
        .then((data) => {
          if (data) {
            processTargetsResponse(data);
          } else {
            Logger.warn('No targets returned for citation');
          }
          pendingRequestRef.current = null;
          setIsLoading(false);
        })
        ?.catch((e) => {
          Logger.error(e);
          setIsLoading(false);
        });
      pendingRequestRef.current = req;
      return req;
    },
    [databaseId, documentIndex, documentRelativeIndex, metadata, minCount, processTargetsResponse, targets.length, totalItems]
  );

  useEffect(() => {
    getTargetsForCitations(true);
    return () => {
      const cancel = get(pendingRequestRef.current, 'cancel');
      if (isFunction(cancel)) {
        cancel();
      }
      pendingRequestRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    const event = new CustomEvent('documentSectionClosed', {
      detail: { closedSection: 'targetsExpanded', focusNext: true },
    });
    document.dispatchEvent(event);
    closeClick();
  };

  const moreTargetsCLick = useCallback(() => getTargetsForCitations(false), [getTargetsForCitations]);

  return (
    <Card
      className="result-detail-panel u-els-margin-top-1o2"
      data-section="targetsExpanded"
      id="targetsExpanded-content"
      tabIndex="-1"
      aria-labelledby="targetsExpanded-heading"
      role="region">
      <div className="result-detail-panel-wrapper" id="targets-wrapper">
        <ButtonWithIcon
          type="link"
          data-testid="targets-wrapper-close"
          className="els-btn-modal-close e2e-close"
          sprite="Close"
          data-e2e="e2e-targets-close"
          linkIconSize="s"
          linkOptions={['navigation']}
          onClick={() => {
            handleClose();
          }}
          iconOnly>
          Close Targets Section
        </ButtonWithIcon>
        <h4 className="u-els-margin-bottom-1o2" id="targetsExpanded-heading">
          Targets
        </h4>
        {targets.map((target, index) => {
          const subunitsLength = get(target, 'subunits.length');
          const targetKey = target.id || target.databaseId || JSON.stringify(target);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${targetKey}-${index}`}>
              <ul>
                <li>
                  <ButtonWithIcon
                    data-e2e="protein-complex-viewdetail-button"
                    sprite="ChevronRight"
                    type="link"
                    color="secondary"
                    iconRight
                    onClick={() => ReactReactionDetailsHelper.goToContext('tgitems', target, metadata)}>
                    {subunitsLength === 1 ? (
                      <InnerHTML
                        tag="span"
                        className="u-els-margin-right-3o4 u-els-font-size-h4"
                        html={ReactTargetHelper.getProteinName(target?.subunits?.[0])}
                      />
                    ) : null}
                    {subunitsLength > 1 && <span className="rx-btn-text u-els-font-size-h4">Protein complex - {subunitsLength}</span>}
                  </ButtonWithIcon>
                </li>
                {get(target, 'subunits', []).map((protein, indexP) => {
                  const proteinKey = protein.id || protein.uniprotId || JSON.stringify(protein);
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <li className="u-els-margin-bottom-1x1o2" key={`${proteinKey}-${indexP}`}>
                      {subunitsLength > 1 ? (
                        <div className="target-external-complex">
                          <InnerHTML
                            tag="span"
                            className="rx-btn-text u-els-font-size-h5 target-external-item u-els-margin-bottom-1x1o2"
                            html={ReactTargetHelper.getProteinName(protein)}
                          />
                        </div>
                      ) : null}
                      <div className="u-els-padding-right">
                        <ul>
                          <li>
                            <Synonyms list={protein.synonyms} prefix="Synonyms: " isDotSeparated showCount />
                          </li>
                        </ul>
                      </div>
                      <div className="u-els-display-flex u-els-margin-top-1x1o2">
                        {protein.uniprotsLinks ? (
                          <UniprotPdbLinks
                            links={protein.uniprotsLinks}
                            heading="Uniprot"
                            headingClass="db-links"
                            useShowMore={false}
                            trackingAction="uniprot"
                            proteinType="single"
                            useSemicolon={false}
                            trackingAttrs={getTrackingAttrs(target, metadata)}
                          />
                        ) : null}
                      </div>
                      <div className="u-els-display-flex u-els-margin-top-1o2">
                        {protein.pdbLinks ? (
                          <UniprotPdbLinks
                            links={protein.pdbLinks}
                            heading="PDB"
                            headingClass="db-links"
                            useShowMore={false}
                            trackingAction="pdb"
                            proteinType="single"
                            useSemicolon={false}
                            trackingAttrs={getTrackingAttrs(target, metadata)}
                          />
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
        {isLoading && <SimpleSpinner className="citations-results-spinner" id={`targets-result-spinner-${documentIndex}`} />}
        {!isLoading && targets.length > 0 && totalItems > targets.length && (
          <div className="els-btn-group-center">
            <ButtonWithIcon
              data-e2e="load-more-button"
              type="tertiary"
              className="e2e-load-more"
              sprite="Plus"
              linkIconSize="xs"
              onClick={moreTargetsCLick}>
              Load More
            </ButtonWithIcon>
          </div>
        )}
      </div>
    </Card>
  );
}

DocumentTargets.propTypes = {
  metadata: MetadataPropTypesShape,
  documentIndex: PropTypes.string.isRequired,
  documentRelativeIndex: PropTypes.string,
  databaseId: PropTypes.string.isRequired,
  targetsCount: PropTypes.number.isRequired,
  closeClick: PropTypes.func.isRequired,
  minCount: PropTypes.number,
};

export default DocumentTargets;
